import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import type { ReceiveConfirmReceiptDto } from '@/entities/receive';
import { DataTable, type DataTableColumn, type DataTableRow } from '@/shared/ui/data-table';

type ReceiptRow = ReceiveConfirmReceiptDto & DataTableRow;

interface ConfirmArrivalReceiptsProps {
  receipts: ReceiveConfirmReceiptDto[];
}

/** A waybill receipt's breakdown: one receipt, and one GRN number, per consignment. */
export function ConfirmArrivalReceipts({ receipts }: ConfirmArrivalReceiptsProps) {
  const { t } = useTranslation();

  const rows = useMemo<ReceiptRow[]>(
    () => receipts.map((receipt) => ({ ...receipt, id: receipt.receiveShipmentId })),
    [receipts]
  );

  const columns = useMemo<DataTableColumn<ReceiptRow>[]>(
    () => [
      {
        id: 'consignmentNumber',
        header: t('request.cmrNumber.short', 'Consignment'),
        cell: (row) => row.consignmentNumber || '—'
      },
      {
        id: 'goodsReceivedNoteNumber',
        header: t('grn.number.label', 'Goods Received Note Number'),
        cell: (row) => (
          <Badge variant="outline" className="font-mono">
            {row.goodsReceivedNoteNumber}
          </Badge>
        )
      },
      {
        id: 'processedLines',
        header: t('receive.convoy.result.lines', 'Lines'),
        width: 'w-24',
        className: 'text-right tabular-nums',
        cell: (row) => row.processedLines
      }
    ],
    [t]
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      tableId="receive-waybill-receipts"
      showToolbar={false}
      enableColumnCustomization={false}
      enableFullscreen={false}
      height="h-auto"
      contentClassName="px-0"
    />
  );
}
